import { RegisterAuth } from '@carvana/core-auth';
import { datadogRum } from '@datadog/browser-rum';
import { useEffect, useRef, useState } from 'react';
import { getAuthInterceptionHosts } from '|/brand';
import { Token, default as getAccessToken } from '|/helpers/access-token';
import { applyCoreAuthHandover, refreshTokenIfMissing } from '|/helpers/axios';
import useAuthSwapoverExperiment from '|/hooks/use-auth-swapover-experiment';

interface Boot {
  isUserInitialized: boolean;
  accessToken: Token | null;
}

interface AuthBoot extends Boot {
  isInTreatment: boolean;
  isExperimentSettled: boolean;
}

// RegisterAuth and the axios handover only run in treatment; refreshTokenIfMissing still covers both arms.
// Never call useCoreAuth here — it subscribes to core-auth's store just by being called, so control must stay out of this hook.
const useAuthBoot = (): AuthBoot => {
  const [boot, setBoot] = useState<Boot>({ isUserInitialized: false, accessToken: null });
  const { isInTreatment, isExperimentLoading } = useAuthSwapoverExperiment();
  const hasRegisteredRef = useRef(false);

  // Independent of the bucket so the refresh isn't delayed behind the experiment lookup.
  useEffect(() => {
    const finish = () => {
      // A failed refresh still initialises — booting tokenless beats hanging on a blank screen.
      setBoot({ isUserInitialized: true, accessToken: getAccessToken() });
    };

    refreshTokenIfMissing().then(finish, finish);
  }, []);

  useEffect(() => {
    if (isExperimentLoading || !isInTreatment || hasRegisteredRef.current) return;
    // Latched here, not at mount: the bucket resolves async, so a mount-only guard never sees treatment.
    hasRegisteredRef.current = true;

    // One array for both calls: core-auth caches normalized hosts in a WeakMap keyed on array identity.
    const interceptionUrls = getAuthInterceptionHosts();

    // Without this a throw unmounts the tree; the session survives on control behaviour instead,
    // still RUM-tagged treatment, so the readout excludes it via this error.
    try {
      RegisterAuth({
        environment: process.env.CVNA_APP_ENVIRONMENT ?? 'production',
        name: 'purchase-ui',
        setupFetch: true,
        // Keep core-auth's own logging in non-prod; it beacons to its own Datadog app.
        disableLogger: process.env.NODE_ENV === 'production',
        // core-auth 2.11.0 defaults this true globally; pin false to avoid a duplicate refresh from another useCoreAuth() consumer.
        refreshTokenIfExpiredOnFirstLoad: false,
        interceptionUrls
      });

      applyCoreAuthHandover(interceptionUrls);
    } catch (error) {
      datadogRum.addError(error, { customType: 'authSwapoverHandoverFailed' });
    }
  }, [isExperimentLoading, isInTreatment]);

  return { ...boot, isInTreatment, isExperimentSettled: !isExperimentLoading };
};

export default useAuthBoot;
